import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

export interface CalendarMeal {
  id: string;
  recipeId?: string;
  recipeName: string;
  mealType: string;
  dayOfWeek?: string;
  scheduledDate?: string;
  plannedServings: number;
  caloriesPerServing?: number;
  totalCalories?: number;
  notes?: string;
}

interface WeeklyMenuCalendarProps {
  meals: CalendarMeal[];
  selectedPlanName?: string;
  selectedPlanType?: string;
  selectedPlanStartDate?: string;
  selectedPlanEndDate?: string;
  onScheduleMeal: (slot: { mealType: string; dayOfWeek: string; scheduledDate?: string }) => void;
  onEditMeal: (meal: CalendarMeal) => void;
  onDeleteMeal: (meal: CalendarMeal) => void;
  onMoveMeal: (meal: CalendarMeal, slot: { mealType: string; dayOfWeek: string; scheduledDate?: string }) => void;
}

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const dayLabels: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

const mealTypes = [
  { key: 'breakfast', label: 'Breakfast', color: '#f9a825' },
  { key: 'lunch', label: 'Lunch', color: '#00838f' },
  { key: 'snack', label: 'Snack', color: '#5e35b1' },
];

const normalize = (value?: string) => (value || '').toLowerCase();

const parseIsoLocal = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const formatIsoLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isoDate = (value?: string) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value.slice(0, 10) : formatIsoLocal(date);
};

const dateLabel = (value: string) =>
  parseIsoLocal(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const dayFromDate = (value: string) => days[parseIsoLocal(value).getDay() - 1] || 'monday';

const weekdaysBetween = (start?: string, end?: string, meals: CalendarMeal[] = []) => {
  const datedMeals = meals.map((meal) => isoDate(meal.scheduledDate)).filter(Boolean).sort();
  const first = start || datedMeals[0];
  const last = end || datedMeals[datedMeals.length - 1];
  if (!first || !last) return [];

  const dates: string[] = [];
  const cursor = parseIsoLocal(first);
  const final = parseIsoLocal(last);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(final.getTime())) return dates;

  while (cursor <= final) {
    const day = cursor.getDay();
    if (day >= 1 && day <= 5) dates.push(formatIsoLocal(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const chunkByWeek = (dates: string[]) => {
  const weeks: string[][] = [];
  let current: string[] = [];
  dates.forEach((date) => {
    if (current.length > 0 && dayFromDate(date) === 'monday') {
      weeks.push(current);
      current = [];
    }
    current.push(date);
  });
  if (current.length > 0) weeks.push(current);
  return weeks;
};

const completeSchoolWeek = (week: string[]) => {
  if (week.length === 0) return [];
  const monday = parseIsoLocal(week[0]);
  monday.setDate(monday.getDate() - (monday.getDay() - 1));
  return days.map((_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return formatIsoLocal(date);
  });
};

const WeeklyMenuCalendar: React.FC<WeeklyMenuCalendarProps> = ({
  meals,
  selectedPlanName,
  selectedPlanType,
  selectedPlanStartDate,
  selectedPlanEndDate,
  onScheduleMeal,
  onEditMeal,
  onDeleteMeal,
  onMoveMeal,
}) => {
  const isDateBasedPlan = normalize(selectedPlanType) === 'monthly' || meals.some((meal) => Boolean(meal.scheduledDate));
  const planStart = selectedPlanStartDate ? isoDate(selectedPlanStartDate) : '';
  const planEnd = selectedPlanEndDate ? isoDate(selectedPlanEndDate) : '';
  const calendarWeeks = chunkByWeek(weekdaysBetween(selectedPlanStartDate, selectedPlanEndDate, meals)).map(completeSchoolWeek);

  const mealsForSlot = (mealType: string, day: string) =>
    meals.filter((meal) =>
      !meal.scheduledDate &&
      normalize(meal.mealType) === mealType &&
      normalize(meal.dayOfWeek) === day
    );

  const mealsForDateSlot = (mealType: string, date: string) =>
    meals.filter((meal) => normalize(meal.mealType) === mealType && isoDate(meal.scheduledDate) === date);

  const getMealSlotStyles = (borderColor: string, hasMeal: boolean) => ({
    backgroundColor: hasMeal ? '#f8f9fa' : '#ffffff',
    p: 1.25,
    borderRadius: 1,
    border: '1px solid',
    borderColor: hasMeal ? '#e0e0e0' : '#d8dde3',
    borderLeft: `4px solid ${borderColor}`,
    minHeight: 112,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  });

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, meal: CalendarMeal) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/json', JSON.stringify({ mealId: meal.id }));
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, mealType: string, dayOfWeek: string, scheduledDate?: string) => {
    event.preventDefault();
    const payload = event.dataTransfer.getData('application/json');
    if (!payload) return;

    const { mealId } = JSON.parse(payload);
    const meal = meals.find((item) => item.id === mealId);
    if (!meal) return;
    if (
      normalize(meal.mealType) === mealType &&
      normalize(meal.dayOfWeek) === dayOfWeek &&
      isoDate(meal.scheduledDate) === (scheduledDate || '')
    ) return;

    onMoveMeal(meal, { mealType, dayOfWeek, scheduledDate });
  };

  const renderMealCard = (meal: CalendarMeal) => (
    <Box
      key={meal.id}
      draggable
      onDragStart={(event) => handleDragStart(event, meal)}
      sx={{
        p: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
            {meal.recipeName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {meal.plannedServings.toLocaleString()} servings
          </Typography>
          {meal.caloriesPerServing != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {meal.caloriesPerServing} kcal / serving
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <Tooltip title="Edit meal">
            <IconButton size="small" onClick={() => onEditMeal(meal)} aria-label="Edit meal">
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove meal">
            <IconButton size="small" color="error" onClick={() => onDeleteMeal(meal)} aria-label="Remove meal">
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {meal.notes && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {meal.notes}
        </Typography>
      )}
    </Box>
  );

  return (
    <Card sx={{ height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6">{isDateBasedPlan ? 'Meal Schedule' : 'Weekly Menu Plan'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedPlanName || 'Select or create a meal plan to schedule recipes'}
            </Typography>
          </Box>
          <Chip label={`${meals.length} scheduled`} size="small" color={meals.length ? 'primary' : 'default'} />
        </Box>

        {meals.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No meals have been scheduled for this plan yet. Use an empty slot or the recipe database to add meals.
          </Alert>
        )}

        {isDateBasedPlan ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1040 }}>
              {calendarWeeks.map((week, index) => (
                <Box key={`${week[0]}-${index}`} sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Week {index + 1}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `128px repeat(${week.length}, minmax(180px, 1fr))`,
                      gap: 1,
                      alignItems: 'stretch',
                    }}
                  >
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Meal
                      </Typography>
                    </Box>
                    {week.map((date) => (
                      <Box
                        key={date}
                        sx={{
                          backgroundColor: date < planStart || date > planEnd ? '#eef1f4' : 'primary.main',
                          color: date < planStart || date > planEnd ? 'text.secondary' : 'primary.contrastText',
                          p: 1.25,
                          borderRadius: 1,
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: 13,
                          minHeight: 64,
                        }}
                      >
                        {dayLabels[dayFromDate(date)]}
                        <Typography component="span" sx={{ display: 'block', fontSize: 11, color: 'inherit', opacity: 0.9 }}>
                          {dateLabel(date)}
                        </Typography>
                      </Box>
                    ))}

                    {mealTypes.map((type) => (
                      <React.Fragment key={`${week[0]}-${type.key}`}>
                        <Box
                          sx={{
                            backgroundColor: type.color,
                            color: 'white',
                            p: 1.25,
                            borderRadius: 1,
                            textAlign: 'center',
                            fontWeight: 700,
                            fontSize: 13,
                            minHeight: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {type.label}
                        </Box>
                        {week.map((date) => {
                          const day = dayFromDate(date);
                          const isInPeriod = (!planStart || date >= planStart) && (!planEnd || date <= planEnd);
                          const slotMeals = mealsForDateSlot(type.key, date);
                          return (
                            <Box
                              key={`${type.key}-${date}`}
                              sx={getMealSlotStyles(type.color, slotMeals.length > 0)}
                              onDragOver={(event) => {
                                event.preventDefault();
                                event.dataTransfer.dropEffect = 'move';
                              }}
                              onDrop={(event) => handleDrop(event, type.key, day, date)}
                            >
                              {!isInPeriod ? (
                                <Typography variant="caption" color="text.secondary">
                                  Outside selected period
                                </Typography>
                              ) : slotMeals.length > 0 ? (
                                slotMeals.map(renderMealCard)
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No meal scheduled
                                </Typography>
                              )}
                              {isInPeriod && (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<AddIcon fontSize="small" />}
                                  onClick={() => onScheduleMeal({ mealType: type.key, dayOfWeek: day, scheduledDate: date })}
                                  sx={{ mt: 'auto', alignSelf: 'flex-start', px: 0.5 }}
                                >
                                  Add
                                </Button>
                              )}
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Grid container spacing={1} sx={{ minWidth: 900 }}>
            <Grid item xs={12} sm={2} md={1.5}>
              <Box sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Meal
                </Typography>
              </Box>
            </Grid>
            {days.map((day) => (
              <Grid item xs={12} sm={2} md={2.1} key={day}>
                <Box
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    p: 1.25,
                    borderRadius: 1,
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {dayLabels[day]}
                </Box>
              </Grid>
            ))}

            {mealTypes.map((type) => (
              <React.Fragment key={type.key}>
                <Grid item xs={12} sm={2} md={1.5}>
                  <Box
                    sx={{
                      backgroundColor: type.color,
                      color: 'white',
                      p: 1.25,
                      borderRadius: 1,
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {type.label}
                  </Box>
                </Grid>
                {days.map((day) => {
                  const slotMeals = mealsForSlot(type.key, day);
                  return (
                    <Grid item xs={12} sm={2} md={2.1} key={`${type.key}-${day}`}>
                      <Box
                        sx={getMealSlotStyles(type.color, slotMeals.length > 0)}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(event) => handleDrop(event, type.key, day)}
                      >
                        {slotMeals.length > 0 ? (
                          slotMeals.map(renderMealCard)
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No meal scheduled
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => onScheduleMeal({ mealType: type.key, dayOfWeek: day })}
                          sx={{ mt: 'auto', alignSelf: 'flex-start', px: 0.5 }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>
                  );
                })}
              </React.Fragment>
            ))}
          </Grid>
        </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyMenuCalendar;
